/**
 * editor2/conflict/fieldState.tsx
 * サイドパネルの各フィールドに「変更中 / 他端末で更新 / 競合」の状態を配るコンテキストと、
 * それを見た目に反映する小部品。
 *
 * 状態の優先順位: conflict > dirty > remote
 *   - dirty    … パネルを開いてから自分が変更した（未保存）
 *   - remote   … パネルを開いた後に他の端末で更新され、自分は触っていないので最新値に更新済み
 *   - conflict … 自分も他の端末も変更した。保存時に確認ダイアログが出る
 */
import React, { createContext, useContext } from 'react';

export type FieldMark = 'dirty' | 'remote' | 'conflict';

export interface FieldStateContextValue {
  marks: Record<string, FieldMark>;
}

export const EditorFieldStateContext = createContext<FieldStateContextValue>({ marks: {} });

export const MARK_LABEL: Record<FieldMark, string> = {
  dirty: '変更',
  remote: '他端末で更新',
  conflict: '競合',
};

/** 枠線・背景・左端バーの色。gridCalendar.scss の .gc-field--* と同じ配色 */
export const MARK_COLORS: Record<FieldMark, { border: string; bg: string; text: string }> = {
  dirty: { border: '#2563eb', bg: '#eff6ff', text: '#1d4ed8' },
  remote: { border: '#0d9488', bg: '#f0fdfa', text: '#0f766e' },
  conflict: { border: '#ea580c', bg: '#fff7ed', text: '#c2410c' },
};

const PRIORITY: FieldMark[] = ['conflict', 'dirty', 'remote'];

/** 複数フィールドをまとめて表示するUI（利用期間 = start_date + end_date 等）向けに、最優先の状態を返す */
export function pickMark(marks: Record<string, FieldMark>, fields?: readonly string[]): FieldMark | null {
  if (!fields || fields.length === 0) return null;
  for (const p of PRIORITY) {
    if (fields.some((f) => marks[f] === p)) return p;
  }
  return null;
}

export function useFieldMark(fields?: readonly string[]): FieldMark | null {
  const { marks } = useContext(EditorFieldStateContext);
  return pickMark(marks, fields);
}

/** `.gc-input` 相当の箱に重ねるインラインスタイル（各コンポーネントは inline style で枠を描いているため） */
export function markBoxStyle(mark: FieldMark | null): React.CSSProperties {
  if (!mark) return {};
  const c = MARK_COLORS[mark];
  return {
    borderColor: c.border,
    background: c.bg,
    boxShadow: `inset 3px 0 0 ${c.border}`,
  };
}

export function FieldMarkChip({ mark }: { mark: FieldMark | null }) {
  if (!mark) return null;
  const c = MARK_COLORS[mark];
  return (
    <span
      className={`gc-field-mark gc-field-mark--${mark}`}
      style={{
        display: 'inline-block',
        marginLeft: 6,
        padding: '0 6px',
        fontSize: '0.68rem',
        fontWeight: 600,
        lineHeight: '16px',
        borderRadius: 8,
        background: c.border,
        color: '#fff',
        verticalAlign: 'middle',
      }}
    >
      {MARK_LABEL[mark]}
    </span>
  );
}

interface FieldMarkWrapProps {
  /** この UI が扱うフィールド名（data-field 属性にも書く。ヘッダーの変更チップからのスクロール先） */
  fields: readonly string[];
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  /** flex 行の中の1要素（ボタン等）を囲む場合。inline-block で左端バーだけ付ける */
  inline?: boolean;
  /** ラベル横にチップを出す（ラベルを持たない UI 向け） */
  chip?: boolean;
}

/**
 * 共有部品（InlineEditField / FieldDisplay）を使っていないセクション向けのラッパー。
 * 状態があれば左端バー + 薄い背景で囲む。無ければ素通し（div は常に出す）。
 */
export function FieldMarkWrap({ fields, children, style, className, inline, chip }: FieldMarkWrapProps) {
  const mark = useFieldMark(fields);
  const c = mark ? MARK_COLORS[mark] : null;
  const markStyle: React.CSSProperties = c
    ? inline
      ? { display: 'inline-flex', alignItems: 'center', borderLeft: `3px solid ${c.border}`, paddingLeft: 4, borderRadius: 4, background: c.bg }
      : { borderLeft: `3px solid ${c.border}`, background: c.bg, borderRadius: 4, paddingLeft: 6, marginLeft: -9 }
    : inline
      ? { display: 'inline-flex', alignItems: 'center' }
      : {};
  return (
    <div
      data-field={fields.join(' ')}
      className={[className, mark ? `gc-field--${mark}` : ''].filter(Boolean).join(' ') || undefined}
      style={{ ...markStyle, ...style }}
    >
      {children}
      {chip && <FieldMarkChip mark={mark} />}
    </div>
  );
}
